import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  Users, 
  Package, 
  Truck, 
  AlertTriangle,
  Bell,
  ArrowRight,
  Clock,
  CheckCircle2,
  Wrench,
  Send,
  Search,
  QrCode,
  TrendingUp,
  TrendingDown,
  Timer,
  Shield,
  BarChart3,
  Activity,
  Calendar,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  PackageCheck,
  Inbox,
  History
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const statusLabels = {
  pendiente_recibir: { label: 'Pendiente Recibir', color: 'bg-amber-500', textColor: 'text-amber-500' },
  recibida: { label: 'Recibida', color: 'bg-blue-500', textColor: 'text-blue-400' },
  en_taller: { label: 'En Taller', color: 'bg-violet-500', textColor: 'text-violet-400' },
  re_presupuestar: { label: 'Re-presupuestar', color: 'bg-orange-500', textColor: 'text-orange-400' },
  reparado: { label: 'Reparado', color: 'bg-green-500', textColor: 'text-green-400' },
  validacion: { label: 'Validación', color: 'bg-indigo-500', textColor: 'text-indigo-400' },
  enviado: { label: 'Enviado', color: 'bg-emerald-500', textColor: 'text-emerald-400' },
  garantia: { label: 'Garantía', color: 'bg-rose-500', textColor: 'text-rose-400' },
  cancelado: { label: 'Cancelado', color: 'bg-slate-500', textColor: 'text-muted-foreground' },
};

/**
 * KPICard — Linear-style minimal stat card.
 * Single accent color via icon tint + subtle hover lift.
 */
function KPICard({ to, icon: Icon, value, label, tone = 'neutral', testId, hoy = null }) {
  // Tone → icon color tint (subtle dot, not full bg)
  const tones = {
    neutral:  'text-muted-foreground bg-muted/40',
    primary:  'text-primary bg-primary/10',
    blue:     'text-blue-400 bg-blue-500/10',
    emerald:  'text-emerald-400 bg-emerald-500/10',
    green:    'text-green-400 bg-green-500/10',
    violet:   'text-violet-400 bg-violet-500/10',
    amber:    'text-amber-400 bg-amber-500/10',
    rose:     'text-rose-400 bg-rose-500/10',
    red:      'text-red-400 bg-red-500/10',
  };
  const card = (
    <div
      className="group relative bg-card border border-border rounded-xl p-3.5 transition-all duration-150 hover:border-primary/40 hover:-translate-y-0.5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone] || tones.neutral}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold tracking-tight text-foreground tabular-nums">{value}</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">ahora</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        {hoy !== null && hoy !== undefined && (
          <span
            className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${
              hoy > 0
                ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/20'
                : 'text-muted-foreground bg-muted/40'
            }`}
            data-testid={testId ? `${testId}-hoy` : undefined}
            title="Movimientos de hoy (se reinicia a las 00:00)"
          >
            {hoy > 0 ? '↑ ' : ''}{hoy} hoy
          </span>
        )}
      </div>
    </div>
  );
  if (!to) return card;
  return <Link to={to} className="block">{card}</Link>;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const scannerInputRef = useRef(null);
  const { isAdmin, isTecnico, isMaster, user } = useAuth();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Scanner states
  const [scannerValue, setScannerValue] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
    // Auto-refresh cada 5 minutos
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);
      
      // Usar endpoint diferente para técnicos
      const endpoint = isTecnico() ? '/dashboard/tecnico' : '/dashboard/operativo';
      const res = await API.get(endpoint);
      setData(res.data);
    } catch (err) {
      console.error('Error cargando dashboard:', err);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => fetchData(true);

  // Scanner logic
  const handleScannerSubmit = async (e) => {
    e.preventDefault();
    if (!scannerValue.trim() || processing) return;
    
    setProcessing(true);
    try {
      const searchValue = scannerValue.trim().toUpperCase();
      const res = await API.get(`/ordenes/buscar?q=${encodeURIComponent(searchValue)}`);
      
      if (res.data.ordenes && res.data.ordenes.length === 1) {
        // Mostrar toast según la acción realizada
        if (res.data.accion === 'recibida') {
          toast.success(res.data.mensaje || 'Orden marcada como RECIBIDA');
        } else {
          toast.info(res.data.mensaje || 'Abriendo orden...');
        }
        navigate(`/crm/ordenes/${res.data.ordenes[0].id}`);
      } else if (res.data.ordenes && res.data.ordenes.length > 1) {
        toast.info(`Se encontraron ${res.data.ordenes.length} órdenes`);
        navigate(`/crm/ordenes?buscar=${encodeURIComponent(searchValue)}`);
      } else {
        toast.error('No se encontró ninguna orden');
      }
    } catch (err) {
      console.error('Error al buscar:', err);
      toast.error(err.response?.data?.detail || 'Error al buscar');
    } finally {
      setProcessing(false);
      setScannerValue('');
    }
  };

  const calcularDiasDesde = (fecha) => {
    if (!fecha) return '-';
    const ahora = new Date();
    const fechaOrden = new Date(fecha);
    const diff = Math.floor((ahora - fechaOrden) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Dashboard específico para técnicos
  if (isTecnico()) {
    const { tecnico, kpis: tecnicoKpis, ordenes: tecnicoOrdenes } = data || {};
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Mi Panel de Técnico</h1>
            <p className="text-muted-foreground text-sm">
              Bienvenido, {tecnico?.nombre || user?.nombre} • {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <form onSubmit={handleScannerSubmit} className="flex gap-2">
              <div className="relative">
                <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  ref={scannerInputRef}
                  placeholder="Escanear código..."
                  value={scannerValue}
                  onChange={(e) => setScannerValue(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <Button type="submit" disabled={processing} size="sm">
                <Search className="w-4 h-4" />
              </Button>
            </form>
            
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* KPIs del técnico */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KPICard icon={ClipboardList} value={tecnicoKpis?.asignadas_actualmente || 0} label="Asignadas Ahora" tone="blue" />
          <KPICard icon={CheckCircle2} value={tecnicoKpis?.total_reparadas_mes || 0} label="Reparadas (30 días)" tone="green" />
          <KPICard icon={Timer} value={`${tecnicoKpis?.promedio_horas || 0}h`} label="Tiempo Promedio" tone="violet" />
          <KPICard icon={Activity} value={`${tecnicoKpis?.promedio_dias?.toFixed(1) || 0}d`} label="Días Promedio" tone="amber" />
        </div>

        {/* Órdenes asignadas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wrench className="w-5 h-5 text-blue-600" />
                Mis Órdenes Asignadas
              </CardTitle>
              <CardDescription>Órdenes que tienes que reparar</CardDescription>
            </CardHeader>
            <CardContent>
              {tecnicoOrdenes?.asignadas?.length > 0 ? (
                <div className="space-y-2">
                  {tecnicoOrdenes.asignadas.map((orden) => (
                    <Link 
                      key={orden.id} 
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/40 hover:bg-accent transition-colors border border-transparent hover:border-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${statusLabels[orden.estado]?.color || 'bg-gray-400'}`} />
                        <div>
                          <p className="font-medium text-sm">{orden.numero_orden}</p>
                          <p className="text-xs text-muted-foreground">
                            {orden.dispositivo?.marca} {orden.dispositivo?.modelo}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {statusLabels[orden.estado]?.label || orden.estado}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No tienes órdenes asignadas</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Últimos reparados */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-green-600" />
                Mis Últimas Reparaciones
              </CardTitle>
              <CardDescription>Historial de tus reparaciones</CardDescription>
            </CardHeader>
            <CardContent>
              {tecnicoOrdenes?.ultimos_reparados?.length > 0 ? (
                <div className="space-y-2">
                  {tecnicoOrdenes.ultimos_reparados.slice(0, 10).map((orden) => (
                    <Link 
                      key={orden.id} 
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/40 hover:bg-accent transition-colors border border-transparent hover:border-border"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <div>
                          <p className="font-medium text-sm">{orden.numero_orden}</p>
                          <p className="text-xs text-muted-foreground">
                            {orden.dispositivo?.marca} {orden.dispositivo?.modelo}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={orden.estado === 'enviado' ? 'default' : 'secondary'} className="text-xs">
                          {statusLabels[orden.estado]?.label || orden.estado}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(orden.updated_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Inbox className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Sin reparaciones recientes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Dashboard para Admin/Master
  const { kpis, en_taller, ordenes, tiempos, graficos } = data || {};

  return (
    <div className="space-y-6">
      {/* Header con escáner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Dashboard Operativo</h1>
          <p className="text-muted-foreground text-sm">
            Control diario del taller • Actualizado: {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <form onSubmit={handleScannerSubmit} className="flex gap-2">
            <div className="relative">
              <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={scannerInputRef}
                placeholder="Escanear código..."
                value={scannerValue}
                onChange={(e) => setScannerValue(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
            <Button type="submit" disabled={processing} size="sm">
              <Search className="w-4 h-4" />
            </Button>
          </form>
          
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs principales - Clicables */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-10 gap-3">
        <KPICard to="/crm/ordenes" icon={ClipboardList} value={kpis?.total_ordenes || 0} label="Total Órdenes" tone="primary" hoy={kpis?.hoy?.creadas} />
        <KPICard to="/crm/ordenes?estado=enviado" icon={Send} value={kpis?.total_enviados || 0} label="Enviados" tone="emerald" hoy={kpis?.hoy?.enviadas} />
        <KPICard to="/crm/ordenes?estado=en_taller" icon={Wrench} value={kpis?.total_en_taller || 0} label="En Taller" tone="violet" hoy={kpis?.hoy?.iniciadas} />
        <KPICard to="/crm/ordenes?estado=pendiente_recibir" icon={Inbox} value={kpis?.total_pendientes_recibir || 0} label="Por Recibir" tone="amber" />
        <KPICard to="/crm/ordenes?estado=recibida" icon={PackageCheck} value={kpis?.total_recibidas || 0} label="Recibidos" tone="blue" testId="dashboard-kpi-recibidos" hoy={kpis?.hoy?.recibidas} />
        <KPICard to="/crm/ordenes?estado=reparado" icon={PackageCheck} value={kpis?.total_reparados || 0} label="Reparados" tone="green" hoy={kpis?.hoy?.reparadas} />
        <KPICard
          to="/crm/ordenes?filtro=demora"
          icon={AlertCircle}
          value={kpis?.con_demora || 0}
          label="Con Demora"
          tone={kpis?.con_demora > 0 ? 'red' : 'neutral'}
        />
        <KPICard to="/crm/ordenes?estado=garantia" icon={Shield} value={kpis?.garantias_activas || 0} label="Garantías" tone="rose" />
        <KPICard icon={Activity} value={kpis?.cambios_hoy || 0} label="Cambios Hoy" tone="blue" />
        <KPICard icon={History} value={kpis?.cambios_ayer || 0} label="Cambios Ayer" tone="neutral" />
      </div>

      {/* Contenido principal - 2 columnas */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Columna izquierda - Estados en taller y métricas */}
        <div className="lg:col-span-2 space-y-6">
          {/* Estados en taller */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-purple-600" />
                    Órdenes en Taller
                  </CardTitle>
                  <CardDescription>Desglose por subestado</CardDescription>
                </div>
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  {en_taller?.total || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/crm/ordenes?estado=recibida')}
                  className="text-left p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 hover:border-blue-500/40 transition-colors cursor-pointer"
                  data-testid="dash-tile-recibidas"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-sm font-medium text-blue-300">Recibidas</span>
                  </div>
                  <span className="text-3xl font-bold text-foreground tabular-nums">{en_taller?.detalle?.recibidas || 0}</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/crm/ordenes?estado=en_taller')}
                  className="text-left p-4 rounded-lg bg-violet-500/10 border border-violet-500/20 hover:bg-violet-500/20 hover:border-violet-500/40 transition-colors cursor-pointer"
                  data-testid="dash-tile-en-reparacion"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-violet-500" />
                    <span className="text-sm font-medium text-violet-300">En Reparación</span>
                  </div>
                  <span className="text-3xl font-bold text-foreground tabular-nums">{en_taller?.detalle?.en_reparacion || 0}</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/crm/ordenes?estado=re_presupuestar')}
                  className="text-left p-4 rounded-lg bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 hover:border-orange-500/40 transition-colors cursor-pointer"
                  data-testid="dash-tile-re-presupuestar"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <span className="text-sm font-medium text-orange-300">Re-presupuestar</span>
                  </div>
                  <span className="text-3xl font-bold text-foreground tabular-nums">{en_taller?.detalle?.re_presupuestar || 0}</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/crm/ordenes?estado=validacion')}
                  className="text-left p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20 hover:border-indigo-500/40 transition-colors cursor-pointer"
                  data-testid="dash-tile-validacion"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-indigo-500" />
                    <span className="text-sm font-medium text-indigo-300">Validación</span>
                  </div>
                  <span className="text-3xl font-bold text-foreground tabular-nums">{en_taller?.detalle?.validacion || 0}</span>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Órdenes con demora */}
          {ordenes?.con_demora?.length > 0 && (
            <Card className="border-rose-500/30 bg-rose-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-rose-400">
                  <AlertCircle className="w-5 h-5" />
                  Órdenes con Demora (+4 días)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ordenes.con_demora.slice(0, 5).map((orden) => (
                    <Link
                      key={orden.id}
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-card border border-border hover:border-rose-500/40 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={statusLabels[orden.estado]?.textColor}>
                          {statusLabels[orden.estado]?.label || orden.estado}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{orden.numero_orden}</p>
                          <p className="text-xs text-muted-foreground">
                            {orden.dispositivo?.modelo || 'Sin modelo'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="destructive" className="text-xs">
                          {orden.dias_demora} días
                        </Badge>
                      </div>
                    </Link>
                  ))}
                  {ordenes.con_demora.length > 5 && (
                    <Link to="/crm/ordenes?filtro=demora" className="flex items-center justify-center gap-2 p-2 text-sm text-rose-400 hover:text-rose-300">
                      Ver todas ({ordenes.con_demora.length})
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Gráfico de órdenes de la semana */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Órdenes esta Semana
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graficos?.ordenes_semana || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="fecha"
                      tickFormatter={(val) => new Date(val).toLocaleDateString('es-ES', { weekday: 'short' })}
                      fontSize={12}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      labelFormatter={(val) => new Date(val).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        color: 'hsl(var(--foreground))',
                      }}
                    />
                    <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Métricas de tiempo */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Timer className="w-5 h-5 text-primary" />
                Métricas de Tiempo
              </CardTitle>
              <CardDescription>
                {tiempos?.metrica_usada === 'mediana' ? 'Mediana' : 'Media'} de los últimos{' '}
                {tiempos?.ventana_dias || 90} días ·{' '}
                {tiempos?.muestras_reparacion || 0} órdenes analizadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-sm text-emerald-300 mb-1 font-medium">Tiempo de Reparación</p>
                  <p className="text-3xl font-bold tabular-nums">
                    {tiempos?.promedio_reparacion_dias?.toFixed(1) || 0}
                    <span className="text-base font-normal text-muted-foreground ml-1">días</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Recibida → Reparado ({tiempos?.muestras_reparacion || 0} muestras)
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-1">Proceso Completo</p>
                  <p className="text-3xl font-bold tabular-nums">
                    {tiempos?.promedio_total_dias?.toFixed(1) || 0}
                    <span className="text-base font-normal text-muted-foreground ml-1">días</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Creación → Envío ({tiempos?.muestras_total || 0} muestras)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha - Listas */}
        <div className="space-y-6">
          {/* Pendientes de recibir */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Inbox className="w-4 h-4 text-yellow-600" />
                  Pendientes de Recibir
                </CardTitle>
                <Badge variant="outline">{ordenes?.pendientes_recibir?.length || 0}</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {ordenes?.pendientes_recibir?.length > 0 ? (
                <div className="space-y-2">
                  {ordenes.pendientes_recibir.slice(0, 5).map((orden) => (
                    <Link
                      key={orden.id}
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-sm">{orden.numero_orden}</p>
                        <p className="text-xs text-muted-foreground">
                          {orden.dispositivo?.modelo || 'Sin modelo'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{orden.agencia_envio || '-'}</p>
                        <p className="text-xs font-mono">{orden.codigo_recogida_entrada?.slice(0, 10) || '-'}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Sin órdenes pendientes</p>
              )}
            </CardContent>
          </Card>

          {/* Recibidos (ya en el centro) - muestra fecha_recibida_centro, NO created_at */}
          <Card data-testid="dashboard-card-recibidos">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <PackageCheck className="w-4 h-4 text-blue-600" />
                  Recibidos
                </CardTitle>
                <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                  {kpis?.total_recibidas || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {ordenes?.ultimas_recibidas?.length > 0 ? (
                <div className="space-y-2">
                  {ordenes.ultimas_recibidas.slice(0, 5).map((orden) => (
                    <Link
                      key={orden.id}
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`dashboard-recibida-${orden.id}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{orden.numero_orden}</p>
                        <p className="text-xs text-muted-foreground">
                          {orden.dispositivo?.modelo || 'Sin modelo'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Recibido
                        </p>
                        <p className="text-xs font-medium text-blue-400">
                          {formatFecha(orden.fecha_recibida_centro || orden.updated_at)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Sin órdenes recibidas</p>
              )}
            </CardContent>
          </Card>

          {/* Últimos reparados */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Últimos Reparados
                </CardTitle>
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                  {kpis?.total_reparados || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {ordenes?.ultimos_reparados?.length > 0 ? (
                <div className="space-y-2">
                  {ordenes.ultimos_reparados.slice(0, 5).map((orden) => (
                    <Link
                      key={orden.id}
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-sm">{orden.numero_orden}</p>
                        <p className="text-xs text-muted-foreground">
                          {orden.dispositivo?.modelo || 'Sin modelo'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{formatFecha(orden.updated_at)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Sin órdenes reparadas</p>
              )}
            </CardContent>
          </Card>

          {/* Últimos enviados */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="w-4 h-4 text-emerald-600" />
                  Últimos Enviados
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {ordenes?.ultimos_enviados?.length > 0 ? (
                <div className="space-y-2">
                  {ordenes.ultimos_enviados.slice(0, 5).map((orden) => (
                    <Link
                      key={orden.id}
                      to={`/crm/ordenes/${orden.id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-sm">{orden.numero_orden}</p>
                        <p className="text-xs text-muted-foreground">
                          {orden.dispositivo?.modelo || 'Sin modelo'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{orden.agencia_envio || '-'}</p>
                        <p className="text-xs">{formatFecha(orden.updated_at)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Sin envíos recientes</p>
              )}
            </CardContent>
          </Card>

          {/* Accesos rápidos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Accesos Rápidos</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" asChild className="justify-start">
                  <Link to="/crm/ordenes/nueva">
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Nueva Orden
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild className="justify-start">
                  <Link to="/crm/clientes">
                    <Users className="w-4 h-4 mr-2" />
                    Clientes
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild className="justify-start">
                  <Link to="/crm/inventario">
                    <Package className="w-4 h-4 mr-2" />
                    Inventario
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild className="justify-start">
                  <Link to="/crm/envios">
                    <Truck className="w-4 h-4 mr-2" />
                    Envíos
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
